/**
 * Cart Drawer Controller
 * Handles slide-in cart drawer matching UpCart reference UI,
 * dynamic AJAX cart updates, steppers, trash removal, and upsells.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Delegated click handler on the entire cart drawer
    this.addEventListener('click', this.handleClick.bind(this));
    
    this.setHeaderCartIconAccessibility();
    window.cartDrawerInstance = this;

    if (window.location.search.includes('open_cart=1')) {
      setTimeout(() => {
        this.open();
      }, 400);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    }
  }

  open() {
    this.classList.remove('is-empty-hidden');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  handleClick(e) {
    // Overlay click
    if (e.target.matches('#CartDrawer-Overlay')) {
      this.close();
      return;
    }

    // Close button click
    const closeBtn = e.target.closest('.cart-drawer__close');
    if (closeBtn) {
      this.close();
      return;
    }

    // Stepper minus
    const minusBtn = e.target.closest('.cart-drawer-stepper__btn.minus');
    if (minusBtn) {
      e.preventDefault();
      const key = minusBtn.dataset.key;
      const line = parseInt(minusBtn.dataset.line, 10);
      const qtyEl = minusBtn.parentElement.querySelector('.cart-drawer-stepper__qty');
      const currentQty = parseInt(qtyEl.textContent.trim(), 10) || 1;
      const newQty = Math.max(0, currentQty - 1);
      this.updateLineQuantity(key || line, newQty);
      return;
    }

    // Stepper plus
    const plusBtn = e.target.closest('.cart-drawer-stepper__btn.plus');
    if (plusBtn) {
      e.preventDefault();
      const key = plusBtn.dataset.key;
      const line = parseInt(plusBtn.dataset.line, 10);
      const qtyEl = plusBtn.parentElement.querySelector('.cart-drawer-stepper__qty');
      const currentQty = parseInt(qtyEl.textContent.trim(), 10) || 1;
      const newQty = currentQty + 1;
      this.updateLineQuantity(key || line, newQty);
      return;
    }

    // Trash can remove
    const trashBtn = e.target.closest('.cart-drawer-item__trash');
    if (trashBtn) {
      e.preventDefault();
      const itemRow = trashBtn.closest('.cart-drawer-item');
      if (itemRow) itemRow.classList.add('is-removing');
      const key = trashBtn.dataset.key;
      const line = parseInt(trashBtn.dataset.line, 10);
      this.updateLineQuantity(key || line, 0);
      return;
    }

    // Upsell quick add
    const upsellBtn = e.target.closest('.cart-drawer__upsell-add-btn');
    if (upsellBtn) {
      e.preventDefault();
      const variantId = upsellBtn.dataset.variantId;
      if (variantId) {
        upsellBtn.disabled = true;
        upsellBtn.textContent = 'Adding...';
        this.addVariantToCart(variantId);
      }
      return;
    }
  }

  updateLineQuantity(target, quantity) {
    const drawerEl = document.getElementById('CartDrawer');
    if (drawerEl) drawerEl.classList.add('is-loading');
    this.classList.add('is-loading');

    const bodyData = {
      quantity: quantity,
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    };
    if (typeof target === 'string' && target.includes(':')) {
      bodyData.id = target;
    } else {
      bodyData.line = parseInt(target, 10);
    }

    fetch(`${window.routes ? window.routes.cart_change_url : '/cart/change.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(bodyData)
    })
      .then(res => res.json())
      .then((parsedState) => {
        if (parsedState && parsedState.sections) {
          this.renderContents(parsedState);
        } else {
          this.refreshAndOpen();
        }
      })
      .catch(err => {
        console.error('Error changing cart quantity:', err);
        this.refreshAndOpen();
      })
      .finally(() => {
        const current = document.getElementById('CartDrawer');
        if (current) current.classList.remove('is-loading');
        this.classList.remove('is-loading');
        this.querySelectorAll('.cart-drawer-item.is-removing').forEach(el => el.classList.remove('is-removing'));
      });
  }

  addVariantToCart(variantId) {
    const drawerEl = document.getElementById('CartDrawer');
    if (drawerEl) drawerEl.classList.add('is-loading');
    this.classList.add('is-loading');

    fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(res => res.json())
      .then(parsedState => {
        if (parsedState && parsedState.sections) {
          this.renderContents(parsedState);
        } else {
          this.refreshAndOpen();
        }
      })
      .catch(err => {
        console.error('Error adding upsell item:', err);
        this.refreshAndOpen();
      })
      .finally(() => {
        const current = document.getElementById('CartDrawer');
        if (current) current.classList.remove('is-loading');
        this.classList.remove('is-loading');
      });
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const target = selector ? doc.querySelector(selector) : doc.body;
    return target ? target.innerHTML : '';
  }

  renderContents(parsedState) {
    try {
      const isEmpty = !parsedState || parsedState.item_count === 0 || (parsedState.items && parsedState.items.length === 0);
      if (isEmpty) {
        this.classList.add('is-empty');
      } else {
        this.classList.remove('is-empty');
      }

      if (parsedState && parsedState.sections && parsedState.sections['cart-drawer']) {
        const drawerHtml = parsedState.sections['cart-drawer'];
        const drawerTarget = document.getElementById('CartDrawer');
        if (drawerTarget) {
          const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
          const newDrawer = doc.querySelector('#CartDrawer');
          if (newDrawer) {
            drawerTarget.innerHTML = newDrawer.innerHTML;
          }
          const newDrawerCustomEl = doc.querySelector('cart-drawer');
          if (newDrawerCustomEl && newDrawerCustomEl.classList.contains('is-empty')) {
            this.classList.add('is-empty');
          } else if (!isEmpty) {
            this.classList.remove('is-empty');
          }
        }

        if (parsedState.sections['cart-icon-bubble']) {
          const bubbleHtml = parsedState.sections['cart-icon-bubble'];
          const bubbleTarget = document.getElementById('cart-icon-bubble');
          if (bubbleTarget) {
            const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
            const newBubble = doc.querySelector('.shopify-section') || doc.body;
            if (newBubble) {
              bubbleTarget.innerHTML = newBubble.innerHTML;
            }
          }
        }

        setTimeout(() => {
          this.open();
        }, 10);
      } else {
        this.refreshAndOpen();
      }
    } catch (e) {
      console.error('CartDrawer renderContents error, falling back to refresh:', e);
      this.refreshAndOpen();
    } finally {
      const current = document.getElementById('CartDrawer');
      if (current) current.classList.remove('is-loading');
      this.classList.remove('is-loading');
      this.querySelectorAll('.cart-drawer-item.is-removing').forEach(el => el.classList.remove('is-removing'));
    }
  }

  refreshAndOpen() {
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${rootUrl}?sections=cart-drawer,cart-icon-bubble&_t=${Date.now()}`)
      .then(res => res.json())
      .then(sections => {
        if (sections['cart-drawer']) {
          const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const newDrawer = doc.querySelector('#CartDrawer');
          const currentDrawer = document.getElementById('CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
          }
          const newDrawerCustomEl = doc.querySelector('cart-drawer');
          if (newDrawerCustomEl && newDrawerCustomEl.classList.contains('is-empty')) {
            this.classList.add('is-empty');
          } else {
            this.classList.remove('is-empty');
          }
        }

        if (sections['cart-icon-bubble']) {
          const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubble = doc.querySelector('.shopify-section') || doc.body;
          const currentBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          }
        }

        this.open();
      })
      .catch(err => {
        console.error('Error refreshing cart drawer:', err);
      })
      .finally(() => {
        const current = document.getElementById('CartDrawer');
        if (current) current.classList.remove('is-loading');
        this.classList.remove('is-loading');
        this.querySelectorAll('.cart-drawer-item.is-removing').forEach(el => el.classList.remove('is-removing'));
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Global helpers
window.openCartDrawer = function() {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && drawer.open) {
    drawer.open();
  }
};

window.refreshAndOpenCartDrawer = function() {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && drawer.refreshAndOpen) {
    drawer.refreshAndOpen();
  }
};
